import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { PROFILE } from "../../data/content";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Instructional Design", to: "/instructional-design" },
  { label: "Photography & Videography", to: "/photography-videography" },
  { label: "Web and Graphic Design", to: "/web-graphic-design" },
  { label: "Interior Design", to: "/interior-design" },
];

const slug = (l) => l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-paper/70 backdrop-blur-xl border-b border-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1500px] px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif text-lg md:text-2xl tracking-tight leading-none text-ink hover:opacity-70 transition-opacity duration-300 whitespace-nowrap"
        >
          Jessica <span className="text-terracotta">Dannielle</span> Johnson
        </Link>

        <div className="hidden lg:flex items-center gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${slug(l.label)}`}
              className={({ isActive }) =>
                `overline transition-colors duration-300 whitespace-nowrap ${
                  isActive
                    ? "text-terracotta underline underline-offset-8 decoration-2 decoration-terracotta/60"
                    : "text-stone hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={PROFILE.resume}
            download
            data-testid="nav-resume-download"
            className="group inline-flex items-center gap-2 rounded-full bg-ink text-paper px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.18em] font-medium hover:bg-terracotta transition-colors duration-300"
          >
            Résumé
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center gap-2 rounded-full border border-ink/25 bg-paper/80 px-4 py-2 text-ink shadow-sm hover:border-ink transition-colors duration-300"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
          <span className="text-[0.7rem] uppercase tracking-[0.2em] font-medium">
            {open ? "Close" : "Menu"}
          </span>
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-paper/95 backdrop-blur-xl border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${slug(l.label)}`}
                  className={({ isActive }) =>
                    `font-serif text-3xl tracking-tight transition-colors ${
                      isActive ? "text-terracotta" : "hover:text-terracotta"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={PROFILE.resume}
                download
                data-testid="nav-mobile-resume"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 text-xs uppercase tracking-[0.18em]"
              >
                Download Résumé
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
